import { useEffect, useRef, useState } from "react";
import cogMascot from "../assets/cog-mascot.png";
import {
  clampCogPosition,
  COG_DISPLAY_SIZE_PX,
  COG_SPEED_PX_PER_SEC,
  getCogBounds,
  randomCogPosition,
  type CogPosition,
  type RandomSource,
} from "./cogMotion";

// Cog remains a subtle background layer (zIndex 0) behind MAIN_PAGE_CONTENT_Z_INDEX.

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return true;
  return (
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false
  );
}

export type FusionSunProps = {
  randomSource?: RandomSource;
};

/**
 * Historical component name retained to avoid widening the App integration
 * change. The old orange canvas sun has been fully replaced by Cog.
 */
export default function FusionSun({
  randomSource = Math.random,
}: FusionSunProps) {
  const cogRef = useRef<HTMLImageElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const [reducedMotion] = useState(() => prefersReducedMotion());
  const [initialPosition] = useState<CogPosition>(() =>
    randomCogPosition({
      containerWidth: window.innerWidth,
      containerHeight: window.innerHeight,
      randomSource,
    }),
  );
  const positionRef = useRef<CogPosition>(initialPosition);

  useEffect(() => {
    const cog = cogRef.current;
    if (!cog) return;

    let width = Math.max(1, window.innerWidth);
    let height = Math.max(1, window.innerHeight);
    const position = positionRef.current;

    const resize = () => {
      width = Math.max(1, window.innerWidth);
      height = Math.max(1, window.innerHeight);
      const clamped = clampCogPosition(
        position,
        getCogBounds({ containerWidth: width, containerHeight: height }),
      );
      position.x = clamped.x;
      position.y = clamped.y;
      cog.style.transform = `translate(${position.x}px, ${position.y}px)`;
    };

    window.addEventListener("resize", resize);
    resize();

    if (reducedMotion) {
      return () => window.removeEventListener("resize", resize);
    }

    const angle = -Math.PI / 4;
    let velocityX = Math.cos(angle) * COG_SPEED_PX_PER_SEC;
    let velocityY = Math.sin(angle) * COG_SPEED_PX_PER_SEC;
    let last = performance.now();

    const step = (now: number) => {
      const deltaSeconds = Math.min(0.05, (now - last) / 1000);
      last = now;
      position.x += velocityX * deltaSeconds;
      position.y += velocityY * deltaSeconds;

      const { minX, maxX, minY, maxY } = getCogBounds({
        containerWidth: width,
        containerHeight: height,
      });
      if (position.x <= minX) {
        position.x = minX;
        velocityX = Math.abs(velocityX);
      } else if (position.x >= maxX) {
        position.x = maxX;
        velocityX = -Math.abs(velocityX);
      }
      if (position.y <= minY) {
        position.y = minY;
        velocityY = Math.abs(velocityY);
      } else if (position.y >= maxY) {
        position.y = maxY;
        velocityY = -Math.abs(velocityY);
      }

      cog.style.transform = `translate(${position.x}px, ${position.y}px)`;
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [reducedMotion]);

  return (
    <img
      ref={cogRef}
      src={cogMascot}
      alt=""
      aria-hidden="true"
      className={`main-page-cog${reducedMotion ? " main-page-cog-static" : ""}`}
      width={COG_DISPLAY_SIZE_PX}
      height={COG_DISPLAY_SIZE_PX}
      draggable={false}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: COG_DISPLAY_SIZE_PX,
        height: COG_DISPLAY_SIZE_PX,
        objectFit: "contain",
        pointerEvents: "none",
        zIndex: 0,
        opacity: reducedMotion ? 0.16 : 0.22,
        // Darken Cog line weight slightly without changing asset bytes or motion.
        filter: "brightness(0.78) contrast(1.12)",
        willChange: reducedMotion ? "auto" : "transform",
        transform: `translate(${initialPosition.x}px, ${initialPosition.y}px)`,
      }}
    />
  );
}
